import { Alert, Badge, Box, Flex, Skeleton, Table, Text } from "@mantine/core";
import { CheckCircleIcon, InfoIcon, WarningIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useSwr from "swr";
import { LedgerApi } from "../../apis";
import { IMemoReport } from "../../apis/ledger";
import { MoneyCell } from "../../components/list";
import { currencyFormat } from "../../utils";
import shared from "../shared/DataDense.module.css";
import classes from "./ledger-table.module.css";
import { ReportPeriod, ReportShell } from "./reports/ReportShell";
import { ListStates } from "../../components/list";

const now = new Date();
const COLS = 3;

/**
 * Хариуцлагат бараа — балансын гадуурх бүртгэл (SPRINT-06 §8.3).
 *
 * ---------------------------------------------------------------------------
 * ЭНЭ БАРАА МАНАЙ ӨМЧ БИШ, ГЭХДЭЭ МАНАЙ ХАРИУЦЛАГА.
 *
 * Консигнацийн бараа эзэмшигч дээр байрлана, өмчлөл нь нийлүүлэгчид үлдэнэ.
 * Тиймээс баланс, ОДТ, шалгах балансаас ЗОРИУДААР хасагдсан (§0.2) — эс
 * бөгөөс групп түвшинд нөөц давхар тоологдоно. Тэр «хасагдсан» нь «үл
 * үзэгдэх» болох ёсгүй: 4.5 сая төгрөгийн бусдын бараа барьж байгаа нь
 * бодит үүрэг. Энэ дэлгэц бол тэр ганц харагдах цэг.
 *
 * ⚠ §8.3-ын «Гэрээ» БАГАНА ЭНД БАЙХГҮЙ, зориудаар. `memoReport` нь гэрээний
 * талаар юу ч мэддэггүй (`MemoRow` = данс · нэр · өмчлөгч · дүн), гэрээ
 * татах resolver нь Алхам 6 бөгөөд §10 Q0b-г хүлээж байна. Байхгүй өгөгдлийг
 * зохиох нь тайланг худал болгоно.
 * ---------------------------------------------------------------------------
 */
export const LedgerMemoPage = () => {
  const { t } = useTranslation(["billing", "common"]);
  const [period, setPeriod] = useState<ReportPeriod>({ year: now.getFullYear(), month: now.getMonth() + 1 });

  const key = `ledger.report.memo.${period.year}.${period.month}`;
  const { data, isLoading, error } = useSwr<IMemoReport>(
    key,
    async () => await LedgerApi.getMemoAccounts(period),
    { revalidateOnFocus: false },
  );

  const rows = data?.rows ?? [];

  return (
    <ReportShell
      title={t("billing:ledger.memo.title")}
      subtitle={t("billing:ledger.memo.subtitle")}
      error={error}
      period={period}
      onPeriodChange={setPeriod}
      banner={
        // Зөвхөн мөртэй тайлан дээр. Хоосон үед `total 0 == contra 0` тул
        // `isBalanced` үнэн болох ч, хоосон хүснэгтийн дээрх ногоон тэмдэг нь
        // хэн ч шалгаагүй зүйлийг баталсан болно (trial-balance-ийн жишиг).
        data && rows.length > 0 && (
          <Badge
            size="lg"
            radius="sm"
            variant="light"
            color={data.isBalanced ? "green" : "red"}
            leftSection={data.isBalanced
              ? <CheckCircleIcon size={13} weight="bold" />
              : <WarningIcon size={13} weight="bold" />}>
            {data.isBalanced
              ? t("billing:ledger.memo.balanced")
              : t("billing:ledger.memo.unbalanced", { diff: currencyFormat(data.difference) })}
          </Badge>
        )
      }>
      <Box className={classes.scroll}>
        {rows.length === 0 ? (
          <ListStates
            loading={isLoading}
            empty
            columns={COLS}
            emptyTitle={t("billing:ledger.memo.empty")}
          />
        ) : (
        <Table className={classes.table} withRowBorders={false} verticalSpacing={0} horizontalSpacing={0}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={classes.th}>{t("billing:ledger.colAccount")}</Table.Th>
              <Table.Th className={classes.th}>{t("billing:ledger.memo.colOwner")}</Table.Th>
              <Table.Th className={classes.th} ta="right">{t("billing:ledger.memo.colAtCost")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(
              <>
                {rows.map((r) => (
                  <Table.Tr key={r.accountId}>
                    <Table.Td>
                      <Text size="sm" ff="monospace" fw={500} className={classes.code}>{r.code}</Text>
                    </Table.Td>
                    <Table.Td>
                      {/*
                        Нэр олдоогүй үед refCode нь өөрөө харагдана. Хоосон нүд
                        нь «өмчлөгчгүй» гэж уншигдана - memo мөр бүр өмчлөгчтэй.
                      */}
                      <Text size="sm" fw={500}>{r.vendorName || r.vendorRef || "-"}</Text>
                      {r.vendorName && r.vendorRef && (
                        <Text size="sm" c="dimmed">{r.vendorRef}</Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">
                      <MoneyCell amount={r.amount} />
                    </Table.Td>
                  </Table.Tr>
                ))}
                <Table.Tr className={classes.subtotalRow}>
                  <Table.Td colSpan={2}>
                    <Text size="sm" fw={500}>{t("billing:ledger.memo.totalRow")}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <MoneyCell amount={data?.total ?? 0} />
                  </Table.Td>
                </Table.Tr>
              </>
            )}
          </Table.Tbody>
        </Table>
        )}
      </Box>

      <Flex align="center" gap={6} px="xs" pt="xs">
        <InfoIcon size={14} weight="bold" />
        <Text size="sm" c="dimmed">{t("billing:ledger.memo.notOursHint")}</Text>
      </Flex>

      {/*
        Нэр нь ТОДОРХОЙЛОГДООГҮЙ мөрүүд. Тэдгээр нь `vendorRef`-ээрээ харагдаж
        байгаа тул мөр нь алга болоогүй - гэхдээ яагаад нэргүй байгааг хэлэхгүй
        бол уншигч тэр бизнесийн нэр бүртгэгдээгүй гэж бодно.
      */}
      {(data?.ambiguousVendorRefs.length ?? 0) > 0 && (
        <Alert color="yellow" radius="md" mt="sm" icon={<WarningIcon size={18} weight="bold" />}>
          {t("billing:ledger.memo.ambiguousOwners", {
            count: data!.ambiguousVendorRefs.length,
            refs : data!.ambiguousVendorRefs.map((x) => `${x.vendorRef} (${x.matches})`).join(", "),
          })}
        </Alert>
      )}

      {/*
        §8.3 нь «Гэрээ» багана зурсан. Түүнийг зохиохын оронд яагаад байхгүйг
        хэлнэ - уншигч дутуу баганыг алдаа гэж бодохгүй, мөн хэзээ гарахыг мэднэ.
      */}
      <Alert color="gray" radius="md" mt="sm" icon={<InfoIcon size={18} weight="bold" />}>
        {t("billing:ledger.memo.contractPending")}
      </Alert>
    </ReportShell>
  );
};

export default LedgerMemoPage;
